import { useCallback, useState } from 'react'
import { TDateRange } from '@pinchenterprisesnpm/friday-ui'
import { isBefore, isSameDay } from 'date-fns'

export const isBeforeToday = (day: Date) => {
  const today = new Date()
  return isBefore(day, today) && !isSameDay(day, today)
}

/**
 * Function that checks whether the user is selecting a day that should be the first day in the date range
 * @param selectedDateRange - selected date range before new day selection
 * @param selectedDay - the day user selected
 * @returns boolean
 */
export const isSelectingFirstDay = (
  selectedDateRange: TDateRange,
  selectedDay?: Date,
) => {
  const { from, to } = selectedDateRange
  const isBeforeFirstDay = !!(from && isBefore(selectedDay as Date, from))
  const isRangeSelected = !!(from && to)

  return !from || isBeforeFirstDay || isRangeSelected
}

/**
 * Function for getting new selected date range depending on the last range selection and new selected day.
 * We are modifying the default behavior of selection in range date picker from react-day-picker.
 * @param selectedDateRange - selected date range before new day selection
 * @param selectedDay - the day user selected
 * @returns TDateRange - new selected date range
 */
export const getNewSelectedDateRange = (
  selectedDateRange: TDateRange,
  selectedDay: Date,
) => {
  if (isSelectingFirstDay(selectedDateRange, selectedDay)) {
    return { from: selectedDay, to: undefined }
  }
  return { from: selectedDateRange.from, to: selectedDay }
}

export const emptyDateRange: TDateRange = {
  from: undefined,
  to: undefined,
}

/**
 * Hook that handles and exposes data and utils for the DatePickerRange component
 * @param initialDateRange Specify the initial date range. Optional.
 */
export const useDatePickerRange = (
  initialDateRange?: TDateRange | (() => TDateRange),
) => {
  const [selectedDateRange, setSelectedDateRange] = useState<TDateRange>(
    initialDateRange || emptyDateRange,
  )

  const onReset = useCallback(() => {
    setSelectedDateRange(emptyDateRange)
  }, [])

  const onSelectRange = useCallback(
    (_: unknown, selectedDay: Date) => {
      setSelectedDateRange(
        getNewSelectedDateRange(selectedDateRange, selectedDay),
      )
    },
    [selectedDateRange],
  )

  const onClose = useCallback(() => {
    const { from, to } = selectedDateRange
    if (from && !to) {
      setSelectedDateRange({ from, to: from })
    }
  }, [selectedDateRange])

  return {
    selectedDateRange,
    onReset,
    onSelectRange,
    onClose,
  }
}
